import { DistanceUnit } from "shared/types";
import { formatDistance } from "shared/utils/formatDistance";
import { Bicycle, Car, Walking } from "iconoir-react";

type TripperDistanceTweenProps = {
  distanceFromPrev?: number | null;
  timeFromPrev?: number | null;
  travelType?: "DRIVING" | "CYCLING" | "WALKING";
  unit?: DistanceUnit;
  showTrailingLine?: boolean;
};

const formatDuration = (seconds?: number | null) => {
  if (!seconds) return "";
  const mins = Math.round(seconds / 60);
  if (mins < 1) return "less than a minute";
  if (mins === 1) return "1 minute";
  if (mins < 60) return `${mins} minutes`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  return remMins > 0 ? `${hours}h ${remMins}m` : `${hours} hour${hours > 1 ? "s" : ""}`;
};

const TravelTypeIcon = ({ travelType = "DRIVING" }: { travelType?: "DRIVING" | "CYCLING" | "WALKING" }) => {
  const commonProps = {
    width: 20,
    height: 20,
    color: "var(--tripper-icon-subtle)",
    strokeWidth: 2,
  };

  if (travelType === "CYCLING") return <Bicycle {...commonProps} />;
  if (travelType === "WALKING") return <Walking {...commonProps} />;
  return <Car {...commonProps} />;
};

export const TripperDistanceTween = ({
  distanceFromPrev,
  timeFromPrev,
  travelType = "DRIVING",
  unit = "metric",
  showTrailingLine = true,
}: TripperDistanceTweenProps) => {
  const hasDistance = (distanceFromPrev ?? 0) > 0;
  const hasTime = (timeFromPrev ?? 0) > 0;
  if (!hasDistance && !hasTime) return null;

  const distStr = hasDistance ? formatDistance(distanceFromPrev ?? 0, unit) : "";
  const timeStr = hasTime ? `(${formatDuration(timeFromPrev)})` : "";
  const label = [distStr, timeStr, "to next stop."].filter(Boolean).join(" ");

  return (
    <div className="tripper-driving-sep">
      <div className="tripper-driving-sep__icon">
        <TravelTypeIcon travelType={travelType} />
      </div>
      <span className="tripper-driving-sep__label">{label}</span>
      {showTrailingLine ? <div className="tripper-driving-sep__line" /> : null}
    </div>
  );
};
